import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Bot, Globe, Building2, Lightbulb, ExternalLink, FileText, Mail, Calendar, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../../types';
import { AppHandoffCard } from './AppHandoffCard';
import './MessageList.css';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  /** Called when a user takes an app handoff. Optional so existing callers
      and tests keep working without wiring telemetry. */
  onAppHandoff?: (appKey: string) => void;
}

const MessageList: React.FC<MessageListProps> = ({ messages, isLoading, onAppHandoff }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastAiMessageRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (!lastMessage.isUser && lastAiMessageRef.current) {
        setTimeout(() => {
          const element = lastAiMessageRef.current;
          if (element) {
            const container = element.closest('.message-list');
            if (container) {
              const elementTop = element.offsetTop;
              container.scrollTo({
                top: elementTop - 20,
                behavior: 'smooth'
              });
            } else {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
              });
            }
          }
        }, 150);
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getGroundingBadgeClass = (source: string): string => {
    if (source === 'agent_bing_grounding' || source === 'bing_search' || source === 'real_time_search') {
      return 'grounding-bing';
    }
    if (source === 'enterprise' || source === 'enterprise_knowledge') {
      return 'grounding-enterprise';
    }
    if (source === 'document_analysis') {
      return 'grounding-document';
    }
    if (source === 'email_analysis') {
      return 'grounding-email';
    }
    if (source === 'calendar_analysis' || source === 'scheduling_analysis') {
      return 'grounding-calendar';
    }
    return 'grounding-general';
  };

  const getGroundingIcon = (source: string) => {
    if (source === 'agent_bing_grounding' || source === 'bing_search' || source === 'real_time_search') {
      return <Globe size={12} />;
    }
    if (source === 'enterprise' || source === 'enterprise_knowledge') {
      return <Building2 size={12} />;
    }
    if (source === 'document_analysis') {
      return <FileText size={12} />;
    }
    if (source === 'email_analysis') {
      return <Mail size={12} />;
    }
    if (source === 'calendar_analysis' || source === 'scheduling_analysis') {
      return <Calendar size={12} />;
    }
    return <Lightbulb size={12} />;
  };

  const getGroundingLabel = (source: string): string => {
    if (source === 'agent_bing_grounding' || source === 'bing_search' || source === 'real_time_search') {
      return 'Bing Search';
    }
    if (source === 'enterprise' || source === 'enterprise_knowledge') {
      return 'Enterprise Knowledge';
    }
    if (source === 'document_analysis') {
      return 'Document Analysis';
    }
    if (source === 'email_analysis') {
      return 'Email';
    }
    if (source === 'calendar_analysis') {
      return 'Calendar';
    }
    if (source === 'scheduling_analysis') {
      return 'Scheduling';
    }
    return 'General Knowledge';
  };

  // react-markdown component overrides — preserve existing CSS classes and
  // force external links to open safely. Replaces a regex-built innerHTML
  // string that was a known XSS vector when the model output reflected
  // attacker-controlled email content.
  const markdownComponents = {
    a: ({ href, children, ...props }: any) => (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    ),
    table: ({ children }: any) => (
      <div className="table-wrapper">
        <table className="markdown-table">{children}</table>
      </div>
    ),
  };

  const handleCopy = async (id: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    } catch {
      // Clipboard API may be blocked; silently no-op rather than alerting.
    }
  };

  return (
    <div className="message-list">
      <div className="messages-container">
        <AnimatePresence>
          {messages.map((message, index) => {
            const isLastAiMessage = !message.isUser && index === messages.length - 1;

            return (
              <motion.div
                key={message.id}
                ref={isLastAiMessage ? lastAiMessageRef : null}
                data-message-id={message.id}
                className={`message ${message.isUser ? 'message-user' : 'message-ai'}`}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.05,
                  ease: [0.4, 0, 0.2, 1]
                }}
              >
              <div className="message-avatar">
                {message.isUser ? (
                  <User size={16} />
                ) : (
                  <Bot size={16} />
                )}
              </div>
              <div className="message-content">
                <div className="message-bubble">
                  {message.isUser ? (
                    <p className="message-text">{message.content}</p>
                  ) : (
                    <>
                      <div className="message-text formatted-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                      <button
                        className="message-copy-button"
                        type="button"
                        onClick={() => handleCopy(message.id, message.content)}
                        aria-label={copiedId === message.id ? 'Copied' : 'Copy response'}
                        title={copiedId === message.id ? 'Copied' : 'Copy response'}
                      >
                        {copiedId === message.id ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      {message.threadSummaries && message.threadSummaries.length > 0 && (
                        <div className="thread-summary-section">
                          <div className="thread-summary-title">Conversation Timeline</div>
                          <ol className="thread-summary-list">
                            {message.threadSummaries.map((entry, idx) => (
                              <li key={entry.messageId || idx} className="thread-summary-item">
                                <div className="thread-summary-header">
                                  <span className="thread-summary-index">{idx + 1}.</span>
                                  <span className="thread-summary-sender">{entry.sender}</span>
                                  <span className="thread-summary-date">
                                    {new Date(entry.sentAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                </div>
                                {entry.subject && (
                                  <div className="thread-summary-subject">Subject: {entry.subject}</div>
                                )}
                                <div className="thread-summary-body">{entry.summary}</div>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {/* Citations */}
                      {message.citations && message.citations.length > 0 && (
                        <div className="citations-section">
                          <div className="citations-title">Sources</div>
                          <div className="citations-list">
                            {message.citations.map((citation, idx) => (
                              <a
                                key={idx}
                                href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="citation-item"
                              >
                                <span className="citation-number">{idx + 1}</span>
                                <span className="citation-text">{citation.title || citation.url}</span>
                                <ExternalLink size={12} />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* App handoff. Rendered after the answer and before the
                          grounding badge: the assistant still answers, and the
                          card offers the app that owns the question. The card
                          re-checks AD group access itself and renders nothing
                          if the user cannot open the app. */}
                      <AppHandoffCard
                        suggestedApp={message.suggestedApp}
                        onOpen={onAppHandoff}
                      />
                      {/* Grounding Badge */}
                      {message.groundingSource && (
                        <div className={`grounding-badge ${getGroundingBadgeClass(message.groundingSource)}`}>
                          {getGroundingIcon(message.groundingSource)}
                          <span>{getGroundingLabel(message.groundingSource)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <span className="message-time">
                    {formatTime(message.timestamp)}
                  </span>
                </div>
              </div>
            </motion.div>
            );
          })}
        </AnimatePresence>

        {isLoading && (
          <motion.div
            className="message message-ai"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="message-avatar">
              <Bot size={16} />
            </div>
            <div className="message-content">
              <div className="message-bubble loading-message">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageList;
